import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Play, Square, Save, RefreshCw, AudioLines, Sliders, FileText, Code, ShieldCheck, Trash2, Sparkles, CircleDot, CalendarClock } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const DEFAULT_TEMPLATE = `[INTRO]
Welcome to today's daily devotion from Bible Baptist Church Barani. Today's title is: {{ title }}.

[SCRIPTURE]
Our scripture reading for today comes from {{ reference }}.

[REFLECTION]
{{ message }}

[APPLICATION]
For our application today: {{ application }}

[OUTRO]
Thank you for joining us. May God bless you as you walk with Him today.`;

const SAMPLE_SCRIPTS = {
  en: "This is a sample script for testing the Kokoro TTS engine. It can eventually be automated from daily devotion data.",
  ilo: "Naimbag nga aldaw kadakayo amin. Ti Dios ket naimbag ken napudno. Agyamantayo iti parabur, ayat, ken pannakabalin nga itedna iti tunggal aldaw.",
};

const DAILY_STATUS_META = {
  draft: { label: "Draft", cls: "bg-[#F3EFEA] text-[#5C6661] border-[#E5E0D8]" },
  review: { label: "Review", cls: "bg-[#D4A373]/20 text-[#8a5a2a] border-[#D4A373]/50" },
  scheduled: { label: "Scheduled", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  published: { label: "Published", cls: "bg-[#2C4C3B]/10 text-[#2C4C3B] border-[#2C4C3B]/30" },
};

const DEFAULT_LANGUAGES = [
  { code: "en", label: "English", provider: "Kokoro ONNX", available: true },
  { code: "ilo", label: "Ilocano", provider: "facebook/mms-tts-ilo", available: false },
];

const SCHEDULER_STATUS_META = {
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  idle: { label: "Idle", cls: "bg-[#F3EFEA] text-[#5C6661] border-[#E5E0D8]" },
  scheduled: { label: "Pending", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  running: { label: "Running", cls: "bg-green-50 text-green-700 border-green-200" },
  stopping: { label: "Stopping", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  completed: { label: "Completed", cls: "bg-[#2C4C3B]/10 text-[#2C4C3B] border-[#2C4C3B]/30" },
  partially_failed: { label: "Partially Failed", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  failed: { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200" },
};

const SCHEDULER_MODE_LABELS = {
  scheduled_batch: "Scheduled Batch",
  time_window: "Time Window",
  start_now: "Start Now",
};

function StatusChip({ status }) {
  const meta = DAILY_STATUS_META[status] || DAILY_STATUS_META.published;
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}><CircleDot className="w-3 h-3" /> {meta.label}</span>;
}

function SchedulerStatusChip({ status }) {
  const meta = SCHEDULER_STATUS_META[status] || SCHEDULER_STATUS_META.idle;
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}><CircleDot className="w-3 h-3" /> {meta.label}</span>;
}

export default function TTSTab() {
  const [voices, setVoices] = useState([]);
  const [settings, setSettings] = useState({
    voice_a: "af_bella",
    voice_b: "",
    weight_a: 100,
    weight_b: 0,
    script: SAMPLE_SCRIPTS.en,
    voice_female: "af_bella",
    voice_male: "am_michael",
    template_content: DEFAULT_TEMPLATE,
    intro_offset: 5.0,
    outro_offset: 5.0,
    speed: 0.8,
    speech_gain: 1.35,
    music_gain: 0.45,
    tts_language: "en",
    site_language: "en",
    ilocano_provider: "facebook/mms-tts-ilo"
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ intro: false, outro: false });
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  const [pendingDevotions, setPendingDevotions] = useState([]);
  const [audioFilter, setAudioFilter] = useState("missing");
  const [loadingPending, setLoadingPending] = useState(false);
  const [generatingDevotionId, setGeneratingDevotionId] = useState(null);
  const [previews, setPreviews] = useState({}); // { devotionId: audioUrl }
  const [narrationMode, setNarrationMode] = useState("template");
  const [directorStatus, setDirectorStatus] = useState(null);
  const [directorDirection, setDirectorDirection] = useState("Warm, clear, pastoral, and concise. Keep the narration natural and suitable for daily listening.");
  const [directorDrafts, setDirectorDrafts] = useState({});
  const [directingId, setDirectingId] = useState(null);
  const [schedulerError, setSchedulerError] = useState("");
  const [schedulerHistory, setSchedulerHistory] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityError, setEligibilityError] = useState("");
  const [schedulerBusy, setSchedulerBusy] = useState(false);
  const [schedulerMode, setSchedulerMode] = useState("scheduled_batch");
  const [schedulerForm, setSchedulerForm] = useState({
    scheduled_start: "",
    item_count: 10,
    window_start: "01:00",
    window_end: "04:00",
    start_now_count: 10,
    regenerate: false,
  });

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/tts/status");
      setStatus(data);
    } catch (e) { console.error(e); }
  };

  const loadVoices = async () => {
    try {
      const { data } = await api.get("/tts/voices");
      setVoices(data || []);
    } catch (e) { console.error(e); }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tts/settings");
      if (data) setSettings(prev => ({ ...prev, ...data }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const { data } = await api.get("/tts/devotion/pending-speech");
      setPendingDevotions(data || []);
      const schedulerPreviews = {};
      (data || []).forEach(d => {
        if (d.preview_audio_url) schedulerPreviews[d.id] = d.preview_audio_url;
      });
      setPreviews(schedulerPreviews);
    } catch (e) { console.error(e); }
    finally { setLoadingPending(false); }
  };

  const loadEligibility = async (regenerate = false) => {
    try {
      const { data } = await api.get("/tts/scheduler/eligibility", { params: { regenerate } });
      setEligibility(data);
      setEligibilityError("");
    } catch (e) {
      setEligibility(null);
      setEligibilityError(e?.response?.data?.detail || "Audio eligibility is unavailable. Refresh to retry.");
    }
  };

  useEffect(() => {
    loadEligibility(schedulerForm.regenerate);
    const timer = setInterval(() => loadEligibility(schedulerForm.regenerate), 15000);
    return () => clearInterval(timer);
  }, [schedulerForm.regenerate]);

  const loadSchedulerStatus = async () => {
    try {
      const { data } = await api.get("/tts/scheduler/status");
      setSchedulerStatus(data);
      setSchedulerError("");
      if (data?.generated && Object.keys(data.generated).length > 0) {
        setPreviews(prev => ({ ...data.generated, ...prev }));
      }
    } catch (e) {
      setSchedulerError(e?.response?.data?.detail || "Scheduler status is unavailable. Refresh to retry.");
    }
  };

  const loadDirectorStatus = async () => {
    try {
      const { data } = await api.get("/tts/narration-director/status");
      setDirectorStatus(data);
    } catch (e) {
      setDirectorStatus({ available: false, reason: "Narration Director status is unavailable." });
    }
  };

  useEffect(() => {
    loadStatus();
    loadVoices();
    loadSettings();
    loadPending();
    loadDirectorStatus();
    loadSchedulerStatus();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      loadSchedulerStatus();
      loadPending();
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const saveSettings = async () => {
    try {
      await api.post("/tts/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    }
  };

  const startScheduler = async () => {
    setSchedulerBusy(true);
    try {
      const regenerate = Boolean(schedulerForm.regenerate);
      if (schedulerMode === "start_now") {
        const { data } = await api.post("/tts/scheduler/start-now", {
          item_count: Number(schedulerForm.start_now_count || 1),
          regenerate,
        });
        setSchedulerStatus(data);
        loadEligibility(regenerate);
        toast.success(data.unfilled ? `Queued ${data.queued} real devotions; ${data.unfilled} requested slots unfilled` : "Audio generation started");
      } else {
        const payload = {
          mode: schedulerMode,
          regenerate,
          item_count: Number(schedulerForm.item_count || 1),
          scheduled_start: schedulerForm.scheduled_start,
          window_start: schedulerForm.window_start,
          window_end: schedulerForm.window_end,
        };
        const { data } = await api.post("/tts/scheduler/schedule", payload);
        setSchedulerStatus(data);
        toast.success(schedulerMode === "time_window" ? "Generation window scheduled" : "Audio generation scheduled");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheduler action failed");
    } finally {
      setSchedulerBusy(false);
    }
  };

  const stopScheduler = async () => {
    setSchedulerBusy(true);
    try {
      const { data } = await api.post("/tts/scheduler/stop");
      setSchedulerStatus(data);
      toast.success(data.status === "idle" ? "Schedule cancelled" : "Scheduler will stop after the current item");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop scheduler");
    } finally {
      setSchedulerBusy(false);
    }
  };

  const cancelScheduler = async () => {
    setSchedulerBusy(true);
    try {
      const { data } = await api.post("/tts/scheduler/cancel");
      setSchedulerStatus(data);
      toast.success("Scheduler cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel scheduler");
    } finally {
      setSchedulerBusy(false);
    }
  };

  const generate = async () => {
    if (!settings.script.trim()) { toast.error("Please enter a script"); return; }
    setGenerating(true);
    setAudioUrl(null);
    try {
      const { data } = await api.post("/tts/generate", {
        text: settings.script,
        voice_a: settings.voice_a,
        voice_b: settings.voice_b || null,
        weight_a: settings.weight_a,
        weight_b: settings.weight_b,
        language: settings.tts_language || "en",
        speed: Number(settings.speed || 0.95)
      });

      if (data.status === "success") {
        toast.success(`${data.language === "ilo" ? "Ilocano" : "English"} preview generated at ${Number(data.speed || settings.speed || 1).toFixed(2)}x`);
        if (data.preview_url) setAudioUrl(data.preview_url);
      } else {
        toast.error(data.message || "Generation failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const generateDevotionSpeech = async (devotionId) => {
    setGeneratingDevotionId(devotionId);
    try {
      const { data } = await api.post(`/tts/devotion/${devotionId}/generate`, {
        language: settings.tts_language || "en",
        speed: Number(settings.speed || 0.95)
      });
      setPreviews(prev => ({ ...prev, [devotionId]: data.audio_url }));
      setAudioFilter("preview");
      toast.success("Preview generated. Play below to check.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate preview");
    } finally {
      setGeneratingDevotionId(null);
    }
  };

  const draftNarration = async (devotionId) => {
    setDirectingId(devotionId);
    try {
      const { data } = await api.post(`/tts/devotion/${devotionId}/narration-draft`, {
        direction: directorDirection,
        script: settings.script,
        template: settings.template_content
      });
      setDirectorDrafts(prev => ({ ...prev, [devotionId]: data.script }));
      toast.success("Narration draft is ready for review.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Narration Director could not create a draft");
    } finally {
      setDirectingId(null);
    }
  };

  const generateDraftSpeech = async (devotionId) => {
    const script = directorDrafts[devotionId]?.trim();
    if (!script) return;
    setGeneratingDevotionId(devotionId);
    try {
      const { data } = await api.post(`/tts/devotion/${devotionId}/generate-script`, {
        script,
        language: settings.tts_language || "en",
        speed: Number(settings.speed || 0.95)
      });
      setPreviews(prev => ({ ...prev, [devotionId]: data.preview_url }));
      setAudioFilter("preview");
      toast.success("Narration preview generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate narration preview");
    } finally {
      setGeneratingDevotionId(null);
    }
  };

  const approveSpeech = async (devotionId) => {
    const url = previews[devotionId];
    if (!url) return;
    try {
      await api.post(`/tts/devotion/${devotionId}/attach`, { audio_url: url });
      toast.success("Audio attached to devotion successfully");
      setPreviews(prev => {
        const next = { ...prev };
        delete next[devotionId];
        return next;
      });
      setPendingDevotions(prev => prev.map(d => d.id === devotionId ? { ...d, audio_url: url, has_audio: true } : d));
      setAudioFilter("missing");
      loadPending();
    } catch (e) {
      toast.error("Failed to attach audio");
    }
  };

  const regenerateDevotionSpeech = async (devotionId) => {
    if (!window.confirm("Remove the attached audio and generate a fresh preview?")) return;
    setGeneratingDevotionId(devotionId);
    try {
      await api.delete(`/tts/devotion/${devotionId}/audio`);
      const { data } = await api.post(`/tts/devotion/${devotionId}/generate`, {
        language: settings.tts_language || "en",
        speed: Number(settings.speed || 0.95)
      });
      setPreviews(prev => ({ ...prev, [devotionId]: data.audio_url }));
      setAudioFilter("preview");
      toast.success("Old audio removed. Fresh preview generated.");
      loadPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate audio");
    } finally {
      setGeneratingDevotionId(null);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [type]: true }));
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post(`/tts/upload-${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${type === 'intro' ? 'Intro' : 'Outro'} music uploaded`);
      loadSettings();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const playPreview = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const audioState = (d) => previews[d.id] || d.preview_audio_url ? "preview" : (d.audio_url || d.has_audio) ? "attached" : "missing";
  const visibleDevotions = pendingDevotions.filter(d => audioFilter === "all" || audioState(d) === audioFilter);
  const languageOptions = settings.languages || status?.languages || DEFAULT_LANGUAGES;
  const selectedLanguage = languageOptions.find(l => l.code === (settings.tts_language || "en")) || DEFAULT_LANGUAGES[0];
  const isIlocanoTts = (settings.tts_language || "en") === "ilo";
  const stockScript = Object.values(SAMPLE_SCRIPTS).includes((settings.script || "").trim());
  const setTtsLanguage = (language) => {
    setAudioUrl(null);
    setSettings(prev => ({
      ...prev,
      tts_language: language,
      script: (!prev.script?.trim() || Object.values(SAMPLE_SCRIPTS).includes(prev.script.trim()))
        ? SAMPLE_SCRIPTS[language]
        : prev.script,
    }));
  };
  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString(undefined, { timeZone: schedulerStatus?.timezone || "Asia/Manila" });
  };
  const currentSchedulerItem = schedulerStatus?.current_item;

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2">
              <Mic className="w-5 h-5 text-[#2C4C3B]" /> Kokoro TTS Settings
            </h3>
            <button
              onClick={saveSettings}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"
            >
              <Save className="w-4 h-4" /> Save Settings
            </button>
          </div>

          {!status?.has_libs && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
              Warning: TTS libraries (onnxruntime, etc.) are not fully installed on the server.
            </div>
          )}

          {settings.tts_language === "ilo" && !selectedLanguage?.available && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
              Ilocano TTS is saved as a CMS option, but the server still needs the optional MMS dependencies for {selectedLanguage?.provider || "facebook/mms-tts-ilo"}.
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Site Language</label>
                <select
                  value={settings.site_language || "en"}
                  onChange={(e) => setSettings({ ...settings, site_language: e.target.value })}
                  className="w-full px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                >
                  {languageOptions.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">TTS Language</label>
                <div className="flex h-[38px] items-center justify-between rounded-lg border border-[#E5E0D8] bg-white px-3">
                  <span className={`text-xs font-bold ${!isIlocanoTts ? "text-[#2C4C3B]" : "text-[#5C6661]"}`}>English</span>
                  <Switch
                    checked={isIlocanoTts}
                    onCheckedChange={(checked) => setTtsLanguage(checked ? "ilo" : "en")}
                    aria-label="Toggle TTS language between English and Ilocano"
                    className="data-[state=checked]:bg-[#2C4C3B] data-[state=unchecked]:bg-[#D4A373]"
                  />
                  <span className={`text-xs font-bold ${isIlocanoTts ? "text-[#2C4C3B]" : "text-[#5C6661]"}`}>Ilocano</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-[#E5E0D8] bg-[#FAF9F6] px-3 py-2 text-[11px] text-[#5C6661]">
              Active engine: <span className="font-bold text-[#1C201E]">{selectedLanguage?.provider || "Kokoro ONNX"}</span>
              {isIlocanoTts && stockScript && <span> - using an Ilocano sample script.</span>}
            </div>

            <div className="rounded-lg border border-[#E5E0D8] bg-white p-3">
              <label className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#5C6661]">
                <span>Speech Speed</span>
                <span className="text-[#1C201E]">{Number(settings.speed || 0.95).toFixed(2)}x</span>
              </label>
              <input
                type="range"
                min="0.55"
                max="1.2"
                step="0.05"
                value={settings.speed || 0.95}
                onChange={(e) => setSettings({ ...settings, speed: parseFloat(e.target.value) })}
                className="w-full h-2 bg-[#F3EFEA] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
              />
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  ["0.70", "Slow"],
                  ["0.80", "Ilocano"],
                  ["0.95", "Natural"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSettings({ ...settings, speed: parseFloat(value) })}
                    className={`rounded-md border px-2 py-1 text-[10px] font-bold ${Math.abs(Number(settings.speed || 0.95) - Number(value)) < 0.01 ? "border-[#2C4C3B] bg-[#2C4C3B] text-white" : "border-[#E5E0D8] bg-[#FAF9F6] text-[#5C6661]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5C6661] mb-2">Voice A (Primary)</label>
              <select
                value={settings.voice_a}
                onChange={(e) => setSettings({ ...settings, voice_a: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
              >
                {voices.length === 0 && <option value="af_bella">af_bella (Default)</option>}
                {voices.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#E5E0D8]">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Female Narrator</label>
                <select
                  value={settings.voice_female || "af_bella"}
                  onChange={(e) => setSettings({ ...settings, voice_female: e.target.value })}
                  className="w-full px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                >
                  {voices.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Male Narrator</label>
                <select
                  value={settings.voice_male || "am_michael"}
                  onChange={(e) => setSettings({ ...settings, voice_male: e.target.value })}
                  className="w-full px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                >
                  {voices.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5C6661] mb-2">Voice B (Secondary - for matching)</label>
              <select
                value={settings.voice_b}
                onChange={(e) => setSettings({ ...settings, voice_b: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
              >
                <option value="">None (Single Voice)</option>
                {voices.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div className="pt-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5C6661] mb-2 flex justify-between">
                <span>Voice Balancing</span>
                <span>{settings.weight_a}% / {settings.weight_b}%</span>
              </label>
              <div className="flex items-center gap-4">
                <span className="text-[10px] font-bold text-[#5C6661]">A</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.weight_a}
                  onChange={(e) => {
                    const wa = parseInt(e.target.value);
                    setSettings({ ...settings, weight_a: wa, weight_b: 100 - wa });
                  }}
                  className="flex-1 h-2 bg-[#F3EFEA] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
                />
                <span className="text-[10px] font-bold text-[#5C6661]">B</span>
              </div>
            </div>

            <div className="pt-4 border-t border-[#E5E0D8]">
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5C6661] mb-2 flex items-center gap-2">
                <Code className="w-3 h-3" /> Script Template (Jinja2)
              </label>
              <textarea
                value={settings.template_content}
                onChange={(e) => setSettings({ ...settings, template_content: e.target.value })}
                rows={8}
                className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-[#FAF9F6] font-mono text-[10px] focus:outline-none focus:ring-2 focus:ring-[#D4A373] resize-none"
                placeholder="Narration template..."
              />
              <p className="text-[9px] text-[#5C6661] mt-1 italic">
                Use [INTRO], [SCRIPTURE], [REFLECTION], [APPLICATION], [OUTRO] tags to switch voices.
                Placeholders: {"{{ title }}, {{ reference }}, {{ message }}, {{ application }}"}
              </p>
            </div>

            <div className="pt-4 border-t border-[#E5E0D8] space-y-4">
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5C6661] mb-2 flex items-center gap-2">
                <Sliders className="w-3 h-3" /> Background Music Overlay
              </label>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-[#5C6661] mb-1">Intro Start Offset (s)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={settings.intro_offset}
                    onChange={(e) => setSettings({ ...settings, intro_offset: parseFloat(e.target.value) })}
                    className="w-full px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5C6661] mb-1">Outro End Offset (s)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={settings.outro_offset}
                    onChange={(e) => setSettings({ ...settings, outro_offset: parseFloat(e.target.value) })}
                    className="w-full px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-[#5C6661] mb-1 flex justify-between">
                    <span>Narration Gain</span>
                    <span>{Number(settings.speech_gain || 1.35).toFixed(2)}x</span>
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={settings.speech_gain || 1.35}
                    onChange={(e) => setSettings({ ...settings, speech_gain: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-[#F3EFEA] rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5C6661] mb-1 flex justify-between">
                    <span>Background Gain</span>
                    <span>{Number(settings.music_gain || 0.45).toFixed(2)}x</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.music_gain ?? 0.45}
                    onChange={(e) => setSettings({ ...settings, music_gain: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-[#F3EFEA] rounded-lg appearance-none cursor-pointer accent-[#D4A373]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-[#5C6661]">Intro MP3</span>
                    {settings.has_intro && <span className="text-[8px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">READY</span>}
                  </div>
                  <label className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#E5E0D8] text-[10px] font-medium cursor-pointer hover:bg-gray-50 ${uploading.intro ? 'opacity-50' : ''}`}>
                    <RefreshCw className={`w-3 h-3 ${uploading.intro ? 'animate-spin' : ''}`} />
                    {uploading.intro ? 'Uploading...' : 'Upload Intro'}
                    <input type="file" accept="audio/mpeg" onChange={(e) => handleFileUpload(e, 'intro')} className="hidden" disabled={uploading.intro} />
                  </label>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-[#5C6661]">Outro MP3</span>
                    {settings.has_outro && <span className="text-[8px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">READY</span>}
                  </div>
                  <label className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#E5E0D8] text-[10px] font-medium cursor-pointer hover:bg-gray-50 ${uploading.outro ? 'opacity-50' : ''}`}>
                    <RefreshCw className={`w-3 h-3 ${uploading.outro ? 'animate-spin' : ''}`} />
                    {uploading.outro ? 'Uploading...' : 'Upload Outro'}
                    <input type="file" accept="audio/mpeg" onChange={(e) => handleFileUpload(e, 'outro')} className="hidden" disabled={uploading.outro} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Script & Preview Panel */}
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl space-y-6">
          <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2C4C3B]" /> Sample Script
          </h3>

          <textarea
            value={settings.script}
            onChange={(e) => setSettings({ ...settings, script: e.target.value })}
            placeholder="Paste a script here to preview or give Narration Director a draft to improve..."
            className="w-full h-48 px-4 py-3 rounded-xl border border-[#E5E0D8] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#D4A373] resize-none text-sm leading-relaxed"
          />

          <div className="flex items-center gap-3">
            <button
              onClick={generate}
              disabled={generating}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-bold hover:bg-[#1A3326] disabled:opacity-60 transition-all shadow-lg shadow-[#2C4C3B]/20"
            >
              {generating ? <RefreshCw className="w-5 h-5 animate-spin" /> : <AudioLines className="w-5 h-5" />}
              {generating ? "Generating..." : "Generate Speech"}
            </button>

            {audioUrl && (
              <div className="flex gap-2">
                <button
                  onClick={playPreview}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4A373] text-white hover:bg-[#b88a5a] transition-all"
                >
                  <Play className="w-6 h-6 fill-current" />
                </button>
                <button
                  onClick={() => toast.success("Audio saved to repository")}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#2C4C3B] text-white hover:bg-[#1A3326] transition-all"
                  title="Save to Repository"
                >
                  <Save className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>

          {audioUrl && (
            <div className="p-4 bg-[#F3EFEA] rounded-xl border border-[#E5E0D8]">
              <audio ref={audioRef} src={audioUrl} controls className="w-full h-8" />
            </div>
          )}
        </div>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#2C4C3B]" /> Audio Generation Scheduler
            </h3>
            <p className="text-xs text-[#5C6661] mt-1">Server-side generation continues after logout. Published devotions needing audio run first, newest first. Valid attachments, pending previews and active jobs are excluded. Audio remains pending attachment; devotion publication status is preserved. Times use the application timezone shown below.</p>
          </div>
          <div className="flex items-center gap-2">
            <SchedulerStatusChip status={schedulerStatus?.status || "idle"} />
            <button onClick={loadSchedulerStatus} className="p-2 rounded-full hover:bg-[#F3EFEA] text-[#5C6661]" title="Refresh Scheduler">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {schedulerError && <p role="alert" className="text-sm text-red-700">{schedulerError}</p>}
        <div className="rounded-lg border border-[#E5E0D8] p-3 text-sm space-y-2">
          <div className="flex items-center justify-between"><strong>Eligible for TTS: {eligibility?.eligible ?? "Checking…"}</strong><button className="underline" onClick={() => loadEligibility(schedulerForm.regenerate)}>Refresh eligibility</button></div>
          {eligibilityError && <p role="alert" className="text-red-700">{eligibilityError}</p>}
          {eligibility && <p className="text-xs">Published needing audio: {eligibility.breakdown?.published_missing_audio ?? 0} · Valid attached audio: {eligibility.breakdown?.already_has_audio ?? 0} · Pending attachment: {eligibility.breakdown?.pending_attachment ?? 0} · Queued/running: {eligibility.breakdown?.queued_running ?? 0}</p>}
          <details><summary>Preview next selections</summary><ol className="list-decimal pl-5 mt-2 text-xs space-y-1">{(eligibility?.next_items || []).slice(0, 10).map(item => <li key={item.id}>{item.date} — {item.title} — {item.publication_status} — Audio missing or invalid</li>)}</ol></details>
          {eligibility?.eligible === 0 && <p>No devotions currently require audio.</p>}
        </div>
        <div className="grid xl:grid-cols-[1fr_1.2fr] gap-5">
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-2">Mode</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["scheduled_batch", "Scheduled"],
                  ["time_window", "Window"],
                  ["start_now", "Now"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setSchedulerMode(value)}
                    className={`h-10 rounded-lg border text-xs font-bold ${schedulerMode === value ? "border-[#2C4C3B] bg-[#2C4C3B] text-white" : "border-[#E5E0D8] bg-[#FAF9F6] text-[#5C6661]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {schedulerMode === "scheduled_batch" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Start Time</label>
                  <input type="datetime-local" value={schedulerForm.scheduled_start} onChange={(e) => setSchedulerForm({ ...schedulerForm, scheduled_start: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Number of Items</label>
                  <input type="number" min="1" max="200" value={schedulerForm.item_count} onChange={(e) => setSchedulerForm({ ...schedulerForm, item_count: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
                </div>
              </div>
            )}

            {schedulerMode === "time_window" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Window Start</label>
                  <input type="time" value={schedulerForm.window_start} onChange={(e) => setSchedulerForm({ ...schedulerForm, window_start: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Window End</label>
                  <input type="time" value={schedulerForm.window_end} onChange={(e) => setSchedulerForm({ ...schedulerForm, window_end: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
                </div>
              </div>
            )}

            {schedulerMode === "start_now" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#5C6661] mb-1">Number of Items to Generate</label>
                <input aria-label="Number of items to generate now" type="number" min="1" max="200" value={schedulerForm.start_now_count} onChange={(e) => setSchedulerForm({ ...schedulerForm, start_now_count: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              </div>
            )}

            <label className="flex items-center justify-between rounded-lg border border-[#E5E0D8] bg-[#FAF9F6] px-3 py-2">
              <span className="text-xs font-bold text-[#1C201E]">Regenerate existing audio previews</span>
              <Switch checked={schedulerForm.regenerate} onCheckedChange={(checked) => setSchedulerForm({ ...schedulerForm, regenerate: checked })} className="data-[state=checked]:bg-[#2C4C3B]" />
            </label>

            <div className="flex flex-wrap gap-2">
              <button onClick={startScheduler} disabled={schedulerBusy || !schedulerStatus || !!schedulerError || !eligibility || !!eligibilityError || (schedulerMode === "start_now" && eligibility.eligible === 0) || schedulerStatus?.status === "running" || schedulerStatus?.status === "scheduled" || schedulerStatus?.status === "stopping"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
                {schedulerBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                {schedulerMode === "start_now" ? "Start Now" : "Start Schedule"}
              </button>
              <button onClick={stopScheduler} disabled={schedulerBusy || !["running", "scheduled"].includes(schedulerStatus?.status)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A373] text-white text-sm hover:bg-[#b88a5a] disabled:opacity-60">
                <Square className="w-4 h-4" /> Stop
              </button>
              <button onClick={cancelScheduler} disabled={schedulerBusy || schedulerStatus?.status === "running"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E5E0D8] bg-white text-[#5C6661] text-sm hover:bg-[#F3EFEA] disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Cancel Schedule
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5E0D8] bg-[#FAF9F6] p-4">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Status</span><SchedulerStatusChip status={schedulerStatus?.status || "idle"} /></div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Mode</span>{SCHEDULER_MODE_LABELS[schedulerStatus?.mode] || "—"}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Schedule</span>{schedulerStatus?.mode === "time_window" ? `${schedulerStatus?.window_start || "—"} – ${schedulerStatus?.window_end || "—"}` : formatDateTime(schedulerStatus?.scheduled_start)}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Next Run</span>{formatDateTime(schedulerStatus?.next_scheduled_run)}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Current</span>{currentSchedulerItem ? `${currentSchedulerItem.date || ""} ${currentSchedulerItem.title || ""}` : "—"}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Started</span>{formatDateTime(schedulerStatus?.started_at)}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Requested</span>{schedulerStatus?.requested ?? schedulerStatus?.item_count ?? "Window"}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Eligible at start</span>{schedulerStatus?.eligible ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Unfilled</span>{schedulerStatus?.unfilled ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Queued</span>{schedulerStatus?.queued ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Running</span>{schedulerStatus?.running ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Generated</span>{schedulerStatus?.successful ?? 0} / {schedulerStatus?.requested ?? schedulerStatus?.item_count ?? "Window"}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Pending attachment</span>{schedulerStatus?.pending_attachment ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Remaining</span>{schedulerStatus?.remaining ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Successful</span>{schedulerStatus?.successful ?? 0}</div>
              <div><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Failed</span>{schedulerStatus?.failed ?? 0}</div>
              <div className="sm:col-span-2"><span className="block text-[10px] uppercase tracking-widest text-[#5C6661] font-bold">Last Activity</span>{formatDateTime(schedulerStatus?.last_activity)}</div>
            </div>
            <div className="mt-3 text-xs text-[#5C6661] break-all">Job ID: {schedulerStatus?.run_id || "—"}</div>
            <div className="mt-3 text-xs text-[#5C6661]">Finished: {formatDateTime(schedulerStatus?.completed_at)} · Scheduled by: {schedulerStatus?.created_by || "—"} · Timezone: {schedulerStatus?.timezone || "Asia/Manila"}</div>
            {schedulerStatus?.failed > 0 && !["running", "scheduled", "stopping"].includes(schedulerStatus?.status) && <button onClick={async () => { try { await api.post("/tts/scheduler/retry"); await loadSchedulerStatus(); toast.success("Failed jobs queued for retry"); } catch(e) { toast.error(e?.response?.data?.detail || "Retry failed"); } }} className="mt-3 rounded-full px-4 py-2 bg-[#2C4C3B] text-white text-sm">Retry failed jobs</button>}
            <div className="mt-4 max-h-64 overflow-auto space-y-2">{(schedulerStatus?.items || []).map((item, index) => <div key={index} className="border rounded-lg p-2 text-xs"><strong>{item.title}</strong> · {item.status === "queued" ? "Pending" : item.status === "success" ? "Completed" : item.status}<br />Started: {formatDateTime(item.started_at)} · Finished: {formatDateTime(item.completed_at)}<br />Item ID: {item.job_id || "—"} · Devotion: {item.devotion_id || "Unfilled"} · Date: {item.date || "—"}<br />Attachment: {item.attachment_status || "Not yet available"}{item.audio && <span> · {item.audio.size} bytes · {Number(item.audio.duration).toFixed(1)} seconds</span>}{item.error && <p className="text-red-700">{item.failure_stage || "Generation"}: {item.error}</p>}{item.audio_url && <a href={item.audio_url} target="_blank" rel="noreferrer" className="underline">Listen to preview</a>}</div>)}</div>
            <button className="mt-3 text-sm underline" onClick={async () => { try { const {data} = await api.get("/tts/scheduler/history"); setSchedulerHistory(data); } catch { toast.error("Could not load history"); } }}>Load run history</button>
            {(schedulerHistory || []).map(run => <details key={run.run_id} className="mt-2 p-2 border rounded text-xs"><summary>{SCHEDULER_MODE_LABELS[run.mode] || run.mode} · {run.status} · Generated {run.successful || 0}/{run.item_count ?? "Window"} · Failed {run.failed || 0}</summary><p className="break-all">Job ID: {run.run_id}</p><p>Eligible: {run.eligible ?? 0} · Unfilled: {run.unfilled ?? 0} · Queued: {run.queued ?? 0} · Running: {run.running ?? 0} · Pending attachment: {run.pending_attachment ?? 0}</p><p>Started: {formatDateTime(run.started_at)} · Completed: {formatDateTime(run.completed_at)} · {run.created_by}</p>{run.last_error && <p className="text-red-700">{run.last_error}</p>}{(run.items || []).map((item, index) => <div key={item.job_id || index} className="border-t mt-2 pt-2"><strong>{item.title}</strong> · {item.date} · {item.status}<p className="break-all">{item.devotion_id} · {item.job_id}</p><p>Attachment: {item.attachment_status || "Not yet available"} · Started: {formatDateTime(item.started_at)} · Finished: {formatDateTime(item.completed_at)}</p>{item.error && <p className="text-red-700">{item.failure_stage}: {item.error}</p>}{item.audio_url && <audio controls preload="none" src={item.audio_url} />}</div>)}</details>)}
            {schedulerStatus?.last_error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">{schedulerStatus.last_error}</div>}
          </div>
        </div>
      </div>

      {/* Devotion Speech Panel */}
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2">
            <AudioLines className="w-5 h-5 text-[#2C4C3B]" /> Devotion Speech Generation
          </h3>
          <div className="flex items-center gap-4">
            <span className="text-[10px] text-gray-400 font-mono">Build v0.1.0-PROD-STABLE</span>
            <button
              onClick={loadPending}
              className="p-2 rounded-full hover:bg-[#F3EFEA] text-[#5C6661]"
              title="Refresh List"
            >
              <RefreshCw className={`w-4 h-4 ${loadingPending ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <p className="text-xs text-[#5C6661]">
          Needs audio shows devotions without attached audio or a generated preview. Generated previews move to Awaiting approval. To regenerate attached audio, delete it in the Devotion tab first.
          Speech uses the Female voice for Intro/Scripture and Male voice for Reflection/Application.
        </p>

        <div className="rounded-xl border border-[#E5E0D8] bg-[#FAF9F6] p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#5C6661]">Narration workflow</div>
              <div className="text-[11px] text-[#5C6661] mt-1">Choose a predictable Jinja template or create an editable Codex CLI draft.</div>
            </div>
            <div className="inline-flex rounded-lg border border-[#E5E0D8] overflow-hidden">
              <button onClick={() => setNarrationMode("template")} className={`px-3 py-2 text-xs font-bold ${narrationMode === "template" ? "bg-[#2C4C3B] text-white" : "bg-white text-[#5C6661]"}`}>Jinja Script</button>
              <button onClick={() => setNarrationMode("director")} className={`px-3 py-2 text-xs font-bold inline-flex items-center gap-1 ${narrationMode === "director" ? "bg-[#2C4C3B] text-white" : "bg-white text-[#5C6661]"}`}><Sparkles className="w-3 h-3" /> Narration Director</button>
            </div>
          </div>
          {narrationMode === "director" && (
            <>
              <textarea value={directorDirection} onChange={(e) => setDirectorDirection(e.target.value)} rows={3} className="w-full rounded-lg border border-[#E5E0D8] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" placeholder="Describe the narration style..." />
              <div className={`text-xs ${directorStatus?.available ? "text-green-700" : "text-amber-700"}`}>
                {directorStatus?.available ? "Codex CLI is ready on this server. Drafts remain editable before audio generation." : (directorStatus?.reason || "Checking Codex CLI availability...")}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2" aria-label="Filter devotions by audio status">
          {[["missing", "Needs audio"], ["preview", "Awaiting approval"], ["attached", "Audio attached"], ["all", "All"]].map(([value, label]) => (
            <button key={value} onClick={() => setAudioFilter(value)} aria-pressed={audioFilter === value}
              className={`px-3 py-2 rounded-full text-xs font-medium ${audioFilter === value ? "bg-[#2C4C3B] text-white" : "bg-[#F3EFEA] text-[#1C201E]"}`}>
              {label} ({pendingDevotions.filter(d => value === "all" || audioState(d) === value).length})
            </button>
          ))}
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {visibleDevotions.map((d) => (
            <div key={d.id} className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{d.date} · {d.reference}</span>
                    <StatusChip status={d.status || "published"} />
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${d.audio_url ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                      {previews[d.id] ? "Audio generated" : d.audio_url ? "Audio attached" : "No audio yet"}
                    </span>
                    {previews[d.id] && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F3EFEA] text-[#5C6661] border border-[#E5E0D8]">Preview awaiting approval</span>}
                  </div>
                  <div className="font-serif-display text-lg text-[#1C201E] mt-1">{d.title}</div>
                  {d.message || d.body || d.content ? (
                    <div className="mt-2 text-sm text-[#2C4C3B] bg-green-50/50 p-2 rounded border border-green-200/50">
                      <span className="font-bold text-[10px] uppercase text-green-700 block mb-1">Devotion Content Found:</span>
                      &ldquo;{d.message || d.body || d.content}&rdquo;
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200 animate-pulse">
                      ⚠️ Warning: Devotion body is empty in database.
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {previews[d.id] ? (
                    <div className="flex items-center gap-2">
                      <audio src={previews[d.id]} controls className="h-8 w-40" />
                      <button
                        onClick={() => approveSpeech(d.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326]"
                      >
                        <ShieldCheck className="w-3 h-3" /> Approve
                      </button>
                      <button
                        onClick={() => setPreviews(p => { const n = {...p}; delete n[d.id]; return n; })}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Discard Preview"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : d.audio_url ? (
                    <div className="flex items-center gap-2">
                      <audio src={d.audio_url} controls className="h-8 w-40" />
                      <button
                        onClick={() => regenerateDevotionSpeech(d.id)}
                        disabled={generatingDevotionId === d.id}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A373] text-white text-xs hover:bg-[#b88a5a] disabled:opacity-60"
                      >
                        {generatingDevotionId === d.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        {generatingDevotionId === d.id ? "Regenerating..." : "Regenerate Audio"}
                      </button>
                    </div>
                  ) : (
                    narrationMode === "director" ? (
                      <button onClick={() => draftNarration(d.id)} disabled={directingId === d.id || !directorStatus?.available} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326] disabled:opacity-60">
                        {directingId === d.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {directingId === d.id ? "Drafting..." : "Draft Narration"}
                      </button>
                    ) : (
                      <button onClick={() => generateDevotionSpeech(d.id)} disabled={generatingDevotionId === d.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326] disabled:opacity-60">
                        {generatingDevotionId === d.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 fill-current" />}
                        {generatingDevotionId === d.id ? "Generating..." : "Generate Audio"}
                      </button>
                    )
                  )}
                </div>
              </div>
              {narrationMode === "director" && directorDrafts[d.id] && !previews[d.id] && (
                <div className="space-y-2 rounded-lg border border-[#D4A373]/40 bg-[#FAF9F6] p-3">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#5C6661]">Review narration before generating audio</label>
                  <textarea value={directorDrafts[d.id]} onChange={(e) => setDirectorDrafts(prev => ({ ...prev, [d.id]: e.target.value }))} rows={10} className="w-full rounded-lg border border-[#E5E0D8] bg-white p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
                  <button onClick={() => generateDraftSpeech(d.id)} disabled={generatingDevotionId === d.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A373] text-white text-xs hover:bg-[#b88a5a] disabled:opacity-60">
                    {generatingDevotionId === d.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 fill-current" />}
                    Generate Audio From Draft
                  </button>
                </div>
              )}
            </div>
          ))}
          {visibleDevotions.length === 0 && !loadingPending && (
            <div className="py-8 text-center text-sm text-[#5C6661] italic">
              {audioFilter === "missing" ? "No devotions need audio in this list." : "No devotions match this audio filter."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
